import createClient, { type Client, type ClientOptions, type Middleware } from 'openapi-fetch';
import { createAuthMiddleware, type TokenProvider } from './auth';
import { createEtagMiddleware, createEtagStore, type EtagStore } from './etag';
import { createSessionRefreshMiddleware } from './session';
import { createTraceparentMiddleware, type TraceparentProvider } from './traceparent';

/**
 * The options every EIS SDK client takes. One shape for every service, because a console that
 * configured two services two different ways would grow a third way for the third service.
 */
export interface EisClientOptions {
  /** Service base URL, e.g. `/api/insurance` behind the reverse proxy, or an absolute origin. */
  readonly baseUrl: string;
  /** Current access token; called per request (see {@link TokenProvider}). */
  readonly tokenProvider?: TokenProvider;
  /**
   * Refreshes the session and resolves the new access token, or null when it is gone (SDD-019
   * S-6, S-11). With it, a 401/403 is refreshed and replayed once and a network error on a
   * replay-safe request is retried; without it, failures reach the caller as they are. The auth
   * adapter supplies it — authservice's `session:refresh` or the legacy refresh endpoint.
   */
  readonly sessionRefresher?: () => Promise<string | null>;
  /** Runs when a refresh fails: the adapter's "this window has no session any more". */
  readonly onSessionLost?: () => void;
  /** The URLs the session middleware must never act on (the session endpoints themselves). */
  readonly isSessionEndpoint?: (url: string) => boolean;
  /** Propagates an existing trace instead of starting one (decision T-13). */
  readonly traceparentProvider?: TraceparentProvider;
  /** Shared with another client to make two clients speak for one belief. Defaults to its own. */
  readonly etagStore?: EtagStore;
  /** Appended after the defaults — for tests and instrumentation, never to replace them. */
  readonly middleware?: readonly Middleware[];
  /** Escape hatch for tests and the mock-api container. */
  readonly fetch?: ClientOptions['fetch'];
}

export interface EisClient<Paths extends object> {
  readonly client: Client<Paths>;
  /** Exposed so a sign-out can drop every belief the previous session established. */
  readonly etags: EtagStore;
}

/**
 * Builds an openapi-fetch client with the platform defaults ALREADY ON: bearer auth, ETag
 * capture / If-Match injection with typed conflicts (T-12), and traceparent (T-13). They are not
 * opt-in — a caller who has to remember to add concurrency control is a caller who ships without
 * it (sdk-generation skill).
 *
 * Order matters. `onRequest` runs first-to-last and `onResponse` last-to-first, so auth and
 * tracing decorate the request before the ETag middleware inspects it, and the ETag middleware
 * sees the response before anything the caller added — it is the one allowed to reject it.
 *
 * The session middleware (SDD-019 S-6), when there is a `sessionRefresher`, is installed LAST
 * (OQ-40): its `onRequest` clones the request after bearer, traceparent and If-Match are on it,
 * so a replay carries all three; its `onResponse` runs FIRST, so a 401/403 is refreshed and
 * replayed before the ETag middleware looks — the ETag middleware sees the FINAL response, and a
 * 412 on the replay still arrives as a typed conflict.
 *
 *   onRequest:  auth → traceparent → extras → ETag → session (clone)
 *   onResponse: session (refresh + replay) → ETag → extras → traceparent → auth
 */
export function createEisClient<Paths extends object>(options: EisClientOptions): EisClient<Paths> {
  const etags = options.etagStore ?? createEtagStore();
  const client = assemble<Paths>(options, [createEtagMiddleware(etags)]);
  return { client, etags };
}

/**
 * The options of a client for ANOTHER team's service — everything `createEisClient` takes except
 * the ETag store: such a service is not party to the platform's If-Match contract (T-12).
 */
export type ForeignClientOptions = Omit<EisClientOptions, 'etagStore'>;

/**
 * A client for another team's service (the Partner service, claimservice): bearer auth, trace
 * propagation (T-13) and the session refresh/replay (SDD-019 S-6) — the same pieces, in the same
 * order, as `createEisClient` — but NO ETag middleware. Such a service emits no ETag, ignores
 * If-Match, and answers its own 409s, which the concurrency middleware would misreport as "the
 * data changed, reload".
 *
 * One function, so a runtime capability added later (as the session middleware was) reaches every
 * foreign client without each SDK re-assembling the chain by hand.
 */
export function createForeignClient<Paths extends object>(
  options: ForeignClientOptions,
): Client<Paths> {
  return assemble<Paths>(options, []);
}

/**
 * The one middleware chain both factories install:
 *   auth → traceparent → caller extras → [ETag, platform services only] → session (last, OQ-40)
 */
function assemble<Paths extends object>(
  options: ForeignClientOptions,
  concurrency: readonly Middleware[],
): Client<Paths> {
  const client = createClient<Paths>({
    baseUrl: options.baseUrl,
    ...(options.fetch === undefined ? {} : { fetch: options.fetch }),
  });

  if (options.tokenProvider !== undefined) {
    client.use(createAuthMiddleware(options.tokenProvider));
  }
  client.use(createTraceparentMiddleware(options.traceparentProvider));
  for (const extra of options.middleware ?? []) client.use(extra);
  for (const middleware of concurrency) client.use(middleware);
  if (options.sessionRefresher !== undefined) {
    client.use(
      createSessionRefreshMiddleware({
        refresh: options.sessionRefresher,
        ...(options.onSessionLost === undefined ? {} : { onSessionLost: options.onSessionLost }),
        ...(options.isSessionEndpoint === undefined
          ? {}
          : { isSessionEndpoint: options.isSessionEndpoint }),
        ...(options.tokenProvider === undefined ? {} : { tokenProvider: options.tokenProvider }),
      }),
    );
  }
  return client;
}
